import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { Card, CommonCss, PageContainer, PageTitle } from 'styles/common.styles';
import { theme } from 'styles/theme';
import {
  Meta,
  Guide,
  ModeTabBar,
  ModeTab,
} from 'components/system/SystemPageChrome.styles';

import { ActionButtonGroup, CollapsibleSearchPanel, FormArea } from 'components/forms';
import { HiGridComponent, hiCol, useHiGridHook } from 'components/grid/hiGrid';
import {
  ActiveGridHeader,
  ActiveGridProvider,
  ActiveGridScope,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import {
  GridExportFocusScope,
  GridExportProvider,
  useExcelExportAction,
  useRegisterGridExport,
} from 'components/grid/excel';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import { showAlert } from 'utils/toastBridge';
import { loadDepts, getCachedDepts } from 'master/deptStore';
import {
  ACCOUNT_STATUS_OPTIONS,
  enrichUserRows,
  filterUsers,
  getCachedUsers,
  loadUsers,
} from 'master/userAccountStore';
import {
  AUTH_MODULE_OPTIONS,
  EXCEPTION_PERMISSION_OPTIONS,
  EXCEPTION_TYPE_OPTIONS,
  RESPONSIBILITY_LEVEL_OPTIONS,
  USER_SCOPE_OPTIONS,
  YN_OPTIONS,
  createPermUid,
  joinDeptNames,
  loadPermissionExceptions,
  loadResponsibilityPermissions,
  savePermissionExceptions,
  saveResponsibilityPermissions,
  validatePermissionExceptions,
  validateResponsibilityPermissions,
} from 'master/permissionStore';

const SEARCH_LABEL_WIDTH = '56px';

const Stack = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 8px;
`;

const Panel = styled(Card)`
  display: flex;
  flex: ${(p) => p.$flex || 1};
  flex-direction: column;
  min-height: 0;
  padding: 0;
  overflow: hidden;
`;

const GridBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;



function todayString() {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function endOfYearString() {
  return `${new Date().getFullYear()}-12-31`;
}

function getPageConfig(mode) {
  if (mode === 'exception') {
    return {
      title: '권한 예외관리',
      summary: '예외 대상 사용자 조회',
      storageKey: 'master-permission-exception',
      userGridId: 'permission-exception-user',
      assignmentGridId: 'permission-exception-detail',
      assignmentLabel: '권한 예외',
      exportFileName: '권한예외관리',
      emptyHint: '사용자를 선택하면 추가·제한 예외를 설정할 수 있습니다.',
      headerExtra: '반복되는 예외는 부서권한 또는 책임자 정책으로 전환',
      guide:
        '제한 예외가 추가 권한보다 우선합니다. 의사결정·전체 권한의 추가는 이 화면에서 허용하지 않으며 책임자 권한설정에서 처리합니다.',
      idField: 'exceptionUid',
      idPrefix: 'PE',
      load: loadPermissionExceptions,
      save: savePermissionExceptions,
      validate: validatePermissionExceptions,
    };
  }
  return {
    title: '책임자 권한설정',
    summary: '책임자 대상 사용자 조회',
    storageKey: 'master-responsibility-permission',
    userGridId: 'permission-responsibility-user',
    assignmentGridId: 'permission-responsibility-detail',
    assignmentLabel: '책임자 권한',
    exportFileName: '책임자권한설정',
    emptyHint: '사용자를 선택하면 의사결정·전체 권한을 설정할 수 있습니다.',
    headerExtra: '승인·확정·마감·반려 등 고위험 권한',
    guide:
      '일반 조회·출력·등록 권한은 부서 권한설정에서 관리합니다. 책임자 권한은 적용기간과 지정 사유를 필수로 기록합니다.',
    idField: 'responsibilityUid',
    idPrefix: 'RP',
    load: loadResponsibilityPermissions,
    save: saveResponsibilityPermissions,
    validate: validateResponsibilityPermissions,
  };
}

/**
 * 책임자 권한설정과 권한 예외관리의 공통 마스터–디테일 화면.
 * 사용자 전체에 권한을 배정하는 용도가 아니라 희소한 책임자·예외 행만 저장한다.
 */
function UserPermissionAssignmentPageInner({ mode }) {
  const config = useMemo(() => getPageConfig(mode), [mode]);
  const { user, isMaster } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const userGridRef = useRef(null);
  const assignmentGridRef = useRef(null);
  const selectedAccountUidRef = useRef('');
  const allAssignmentsRef = useRef([]);
  const lookupTargetRef = useRef('');
  const [selectedHint, setSelectedHint] = useState(config.emptyHint);
  const [deptLookupOpen, setDeptLookupOpen] = useState(false);

  const searchDefaults = useMemo(
    () => ({ tenantCode, loginId: '', userNm: '', status: 'ACTIVE' }),
    [tenantCode]
  );
  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue } = methods;

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [setValue, tenantCode]);

  const [deptRows, setDeptRows] = useState(() =>
    (getCachedDepts(tenantCode) || []).filter((r) => r.useYn !== 'N')
  );
  useEffect(() => {
    let cancelled = false;
    loadDepts(tenantCode)
      .then((rows) => {
        if (!cancelled) setDeptRows((rows || []).filter((r) => r.useYn !== 'N'));
      })
      .catch(() => {
        if (!cancelled) setDeptRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantCode]);

  const userColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '사용자ID', field: 'loginId', size: 110 }),
      hiCol.roTextColumn({ header: '사용자명', field: 'userNm', size: 100 }),
      hiCol.roTextColumn({ header: '주관부서', field: 'deptName', size: 140 }),
      hiCol.roTextColumn({ header: '계정유형', field: 'accountTypeNm', size: 100, align: 'center' }),
      hiCol.roTextColumn({ header: '계정상태', field: 'statusNm', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '비고', field: 'remark', size: 180 }),
    ],
    []
  );

  const openDeptLookup = useCallback(
    (row) => {
      const rowUid = row?.[config.idField];
      if (!rowUid) return;
      if (row.scopeType !== 'PICK') {
        showAlert('데이터범위를 「선택부서」로 지정한 행에서만 대상부서를 선택합니다.', 'warning');
        return;
      }
      lookupTargetRef.current = rowUid;
      setDeptLookupOpen(true);
    },
    [config.idField]
  );

  const responsibilityColumns = useMemo(
    () => [
      hiCol.edComboColumn({
        header: '모듈',
        field: 'moduleCode',
        size: 110,
        options: AUTH_MODULE_OPTIONS,
        required: true,
      }),
      hiCol.edComboColumn({
        header: '권한구성',
        field: 'authorityLevel',
        size: 110,
        options: RESPONSIBILITY_LEVEL_OPTIONS,
        required: true,
      }),
      hiCol.edComboColumn({
        header: '데이터범위',
        field: 'scopeType',
        size: 140,
        options: USER_SCOPE_OPTIONS,
        required: true,
      }),
      hiCol.edTextColumn({
        header: '선택부서',
        field: 'deptNamesDisp',
        size: 190,
        editable: false,
        showSearch: true,
        searchTitle: '대상부서 선택',
        onSearch: ({ row }) => openDeptLookup(row),
      }),
      hiCol.edDateColumn({
        header: '시작일',
        field: 'startDate',
        size: 115,
        required: true,
      }),
      hiCol.edDateColumn({
        header: '종료일',
        field: 'endDate',
        size: 115,
        required: true,
      }),
      hiCol.edComboColumn({ header: '사용', field: 'useYn', size: 70, options: YN_OPTIONS }),
      hiCol.edTextColumn({
        header: '지정사유',
        field: 'reason',
        size: 220,
        required: true,
      }),
    ],
    [openDeptLookup]
  );

  const exceptionColumns = useMemo(
    () => [
      hiCol.edComboColumn({
        header: '예외유형',
        field: 'exceptionType',
        size: 90,
        options: EXCEPTION_TYPE_OPTIONS,
        required: true,
      }),
      hiCol.edComboColumn({
        header: '모듈',
        field: 'moduleCode',
        size: 105,
        options: AUTH_MODULE_OPTIONS,
        required: true,
      }),
      hiCol.edComboColumn({
        header: '권한구성',
        field: 'permissionType',
        size: 110,
        options: EXCEPTION_PERMISSION_OPTIONS,
        required: true,
      }),
      hiCol.edComboColumn({
        header: '데이터범위',
        field: 'scopeType',
        size: 135,
        options: USER_SCOPE_OPTIONS,
        required: true,
      }),
      hiCol.edTextColumn({
        header: '선택부서',
        field: 'deptNamesDisp',
        size: 170,
        editable: false,
        showSearch: true,
        searchTitle: '대상부서 선택',
        onSearch: ({ row }) => openDeptLookup(row),
      }),
      hiCol.edDateColumn({
        header: '시작일',
        field: 'startDate',
        size: 115,
        required: true,
      }),
      hiCol.edDateColumn({
        header: '종료일',
        field: 'endDate',
        size: 115,
        required: true,
      }),
      hiCol.edComboColumn({ header: '사용', field: 'useYn', size: 70, options: YN_OPTIONS }),
      hiCol.edTextColumn({
        header: '예외사유',
        field: 'reason',
        size: 210,
        required: true,
      }),
    ],
    [openDeptLookup]
  );

  const assignmentColumns =
    mode === 'exception' ? exceptionColumns : responsibilityColumns;

  const userGrid = useHiGridHook({
    gridRef: userGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
    defaultRow: {},
  });

  const assignmentGrid = useHiGridHook({
    gridRef: assignmentGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: {},
  });

  const withDisplay = useCallback(
    (row) => ({ ...row, deptNamesDisp: joinDeptNames(row.deptNames) }),
    []
  );

  const mergeCurrentRows = useCallback(() => {
    const accountUid = selectedAccountUidRef.current;
    if (!accountUid) return;
    const current = assignmentGridRef.current?.getData?.() || [];
    allAssignmentsRef.current = [
      ...allAssignmentsRef.current.filter((r) => r.accountUid !== accountUid),
      ...current.map((r) => ({ ...r, accountUid })),
    ];
  }, []);

  const selectUser = useCallback(
    (row) => {
      if (!row?.accountUid) return;
      if (
        selectedAccountUidRef.current &&
        selectedAccountUidRef.current !== row.accountUid
      ) {
        mergeCurrentRows();
      }
      selectedAccountUidRef.current = row.accountUid;
      setSelectedHint(
        `${row.loginId || ''} · ${row.userNm || ''}${
          row.deptName ? ` · 주관 ${row.deptName}` : ''
        }`
      );
      assignmentGridRef.current?.setData?.(
        allAssignmentsRef.current
          .filter((r) => r.accountUid === row.accountUid)
          .map(withDisplay)
      );
    },
    [mergeCurrentRows, withDisplay]
  );

  const handleSearch = useCallback(async () => {
    const users = enrichUserRows(await loadUsers(tenantCode, user));
    allAssignmentsRef.current = config.load(tenantCode);
    const filtered = filterUsers(users, getValues());
    userGridRef.current?.setData?.(filtered);
    const selected = filtered.find(
      (r) => r.accountUid === selectedAccountUidRef.current
    );
    if (selected) {
      selectedAccountUidRef.current = '';
      selectUser(selected);
      return;
    }
    selectedAccountUidRef.current = '';
    setSelectedHint(config.emptyHint);
    assignmentGridRef.current?.setData?.([]);
  }, [config, getValues, selectUser, tenantCode, user]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 화면 진입 시 최초 조회
  }, []);

  useEffect(() => {
    userGrid.onSelectRow = (row) => selectUser(row);
    userGrid.onRowClicked = ({ data }) => selectUser(data);
  }, [selectUser, userGrid]);

  const handleAssignmentAdd = useCallback(async () => {
    const accountUid = selectedAccountUidRef.current;
    if (!accountUid) {
      showAlert('사용자를 먼저 선택하세요.', 'warning');
      return;
    }
    const common = {
      [config.idField]: createPermUid(config.idPrefix),
      accountUid,
      moduleCode: AUTH_MODULE_OPTIONS[0].value,
      scopeType: 'OWN',
      deptUids: [],
      deptCodes: [],
      deptNames: [],
      deptNamesDisp: '',
      startDate: todayString(),
      endDate: endOfYearString(),
      useYn: 'Y',
      reason: '',
    };
    await assignmentGrid.handleAdd(
      mode === 'exception'
        ? { ...common, exceptionType: 'DENY', permissionType: 'READ' }
        : { ...common, authorityLevel: 'DECISION' }
    );
  }, [assignmentGrid, config.idField, config.idPrefix, mode]);

  const handleAssignmentDelete = useCallback(async () => {
    await assignmentGrid.handleDelete();
  }, [assignmentGrid]);

  const { handleActiveAdd, handleActiveDelete, activeGridId, activeLabel } =
    useActiveGridCrudActions({
      guardAdd: (id) => {
        if (id === config.userGridId) return '계정 등록은 사용자관리 화면에서 하세요.';
        if (!selectedAccountUidRef.current) return '사용자를 먼저 선택하세요.';
        return null;
      },
      guardDelete: (id) => {
        if (id === config.userGridId) return '계정 삭제는 사용자관리 화면에서 하세요.';
        if (!selectedAccountUidRef.current) return '사용자를 먼저 선택하세요.';
        return null;
      },
    });

  const applyDeptLookup = useCallback(
    (dept) => {
      const target = lookupTargetRef.current;
      if (!target || !dept?.deptUid) return;
      const rows = assignmentGridRef.current?.getData?.() || [];
      const index = rows.findIndex((r) => r[config.idField] === target);
      if (index < 0) return;
      const row = rows[index];
      const deptUids = [...(row.deptUids || [])];
      const deptCodes = [...(row.deptCodes || [])];
      const deptNames = [...(row.deptNames || [])];
      const found = deptUids.indexOf(dept.deptUid);
      if (found >= 0) {
        deptUids.splice(found, 1);
        deptCodes.splice(found, 1);
        deptNames.splice(found, 1);
      } else {
        deptUids.push(dept.deptUid);
        deptCodes.push(dept.deptCode || '');
        deptNames.push(dept.deptName || '');
      }
      assignmentGridRef.current?.setCellValues?.(index, {
        deptUids,
        deptCodes,
        deptNames,
        deptNamesDisp: joinDeptNames(deptNames),
      });
    },
    [config.idField]
  );

  const handleSave = useCallback(() => {
    mergeCurrentRows();
    const users = getCachedUsers(tenantCode);
    const rows = allAssignmentsRef.current.filter((r) => r.mod !== 'D');
    const validation = config.validate(rows, users);
    if (!validation.ok) {
      showAlert(validation.message, 'warning');
      return;
    }
    allAssignmentsRef.current = config.save(tenantCode, rows);
    assignmentGridRef.current?.setData?.(
      allAssignmentsRef.current
        .filter((r) => r.accountUid === selectedAccountUidRef.current)
        .map(withDisplay)
    );
    showAlert(`${config.assignmentLabel}을 저장했습니다.`, 'success');
  }, [config, mergeCurrentRows, tenantCode, user, withDisplay]);

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'loginId',
          label: '사용자ID',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_WIDTH,
        },
        {
          name: 'userNm',
          label: '사용자명',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_WIDTH,
        },
        {
          name: 'status',
          label: '계정상태',
          type: 'select',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_WIDTH,
          options: [{ value: '', label: '전체' }, ...ACCOUNT_STATUS_OPTIONS],
        },
      ],
    ],
    []
  );

  const getUserExportData = useCallback(
    () => userGridRef.current?.getFilteredData?.() ?? userGridRef.current?.getData?.() ?? [],
    []
  );
  const getUserExportColumns = useCallback(
    () => userGridRef.current?.getDataColumns?.() ?? [],
    []
  );
  const getAssignmentExportData = useCallback(
    () =>
      assignmentGridRef.current?.getFilteredData?.() ??
      assignmentGridRef.current?.getData?.() ??
      [],
    []
  );
  const getAssignmentExportColumns = useCallback(
    () => assignmentGridRef.current?.getDataColumns?.() ?? [],
    []
  );

  useRegisterGridExport({
    id: config.userGridId,
    label: '사용자',
    getData: getUserExportData,
    getDataColumns: getUserExportColumns,
    joinKeys: ['accountUid'],
    order: 0,
  });
  useRegisterGridExport({
    id: config.assignmentGridId,
    label: config.assignmentLabel,
    getData: getAssignmentExportData,
    getDataColumns: getAssignmentExportColumns,
    joinKeys: [config.idField],
    order: 1,
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: config.exportFileName,
  });

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      {
        id: 'add',
        label: '추가',
        icon: '➕',
        onClick: handleActiveAdd,
        hotkey: 'F8',
        title: activeLabel ? `추가 → ${activeLabel} (F8)` : '추가 (F8)',
      },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
        title: activeLabel ? `삭제 → ${activeLabel} (F9)` : '삭제 (F9)',
      },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSave,
        hotkey: 'F10',
        variant: 'success',
      },
      { id: 'excel', label: '엑셀', icon: '📥', onClick: openExcelDialog, hotkey: 'F11' },
    ],
    [
      activeLabel,
      handleActiveAdd,
      handleActiveDelete,
      handleSave,
      handleSearch,
      openExcelDialog,
    ]
  );

  const lookupColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 110 }),
      hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 160 }),
      hiCol.roTextColumn({ header: '경로', field: 'pathCode', size: 200 }),
    ],
    []
  );
  const lookupRows = useMemo(
    () => deptRows.map((r) => ({ ...r, id: r.deptUid })),
    [deptRows]
  );

  if (!isMaster) {
    return (
      <PageContainer>
        <PageTitle>{config.title}</PageTitle>
        <Meta>기관 관리자 전용 화면입니다. 접근 권한이 없습니다.</Meta>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} />
        <CollapsibleSearchPanel
          storageKey={config.storageKey}
          prefsUserId={prefsUserId}
          summary={config.summary}
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Meta>
        활성 그리드: <strong style={{ color: theme.colors.primary }}>{activeLabel || '—'}</strong>
        {' · '}Alt+F2 순환{activeGridId ? ` · F8/F9 → ${activeLabel}` : ''}
        {' · '}사용자 직접 지정은 책임자·예외에만 사용
      </Meta>

      <Stack>
        <Panel $flex={1}>
          <ActiveGridHeader gridId={config.userGridId} extra="사용자관리 계정 · 조회 전용">
            사용자
          </ActiveGridHeader>
          <GridBody>
            <ActiveGridScope
              gridId={config.userGridId}
              label="사용자"
              order={0}
              canAdd={false}
              canDelete={false}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope
                gridId={config.userGridId}
                style={{ flex: 1, minHeight: 0, height: '100%' }}
              >
                <HiGridComponent
                  ref={userGridRef}
                  columns={userColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  headerActive={activeGridId === config.userGridId}
                  enableColumnFilters
                  enableHeaderMenu
                  enableSelectionStats
                  enableFreeze
                  enableQuickExport
                  exportFileName="사용자"
                  gridId={config.userGridId}
                  prefsUserId={prefsUserId}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>

        <Panel $flex={0.95}>
          <ActiveGridHeader
            gridId={config.assignmentGridId}
            extra={config.headerExtra}
          >
            {config.assignmentLabel}
          </ActiveGridHeader>
          <Meta style={{ padding: '4px 12px' }}>{selectedHint}</Meta>
          <GridBody>
            <ActiveGridScope
              gridId={config.assignmentGridId}
              label={config.assignmentLabel}
              order={1}
              onAdd={handleAssignmentAdd}
              onDelete={handleAssignmentDelete}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope
                gridId={config.assignmentGridId}
                style={{ flex: 1, minHeight: 0, height: '100%' }}
              >
                <HiGridComponent
                  ref={assignmentGridRef}
                  columns={assignmentColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  showModColumn
                  headerActive={activeGridId === config.assignmentGridId}
                  {...assignmentGrid.contextMenuProps}
                  enableColumnFilters
                  enableHeaderMenu
                  enableSelectionStats
                  enableFreeze
                  enableQuickExport
                  exportFileName={config.assignmentLabel}
                  gridId={config.assignmentGridId}
                  prefsUserId={prefsUserId}
                  colOptions={assignmentGrid.colOptions}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>

        <Guide>{config.guide}</Guide>
      </Stack>

      <CodeLookupDialog
        open={deptLookupOpen}
        title="데이터범위 대상부서 선택"
        rows={lookupRows}
        columns={lookupColumns}
        enableSearch
        searchConfig={{
          codeFields: ['deptCode'],
          nameFields: ['deptName'],
          codeLabel: '부서코드',
          nameLabel: '부서명',
        }}
        onSelect={applyDeptLookup}
        onClose={() => setDeptLookupOpen(false)}
      />
      {excelDialog}
    </PageContainer>
  );
}

export default function UserPermissionAssignmentPage({ mode }) {
  return (
    <GridExportProvider>
      <ActiveGridProvider>
        <UserPermissionAssignmentPageInner mode={mode} />
      </ActiveGridProvider>
    </GridExportProvider>
  );
}
